import { apiFetch } from "./client";

export interface Agent {
  id: string;
  companyId: string;
  name: string;
  role: string;
  title: string | null;
  icon: string | null;
  department: string | null;
  avatarUrl: string | null;
  status: string;
  runtimeTier: string;
  reportsTo: string | null;
  parentAgentId: string | null;
  capabilities: string | null;
  adapterType: string;
  adapterConfig: Record<string, unknown> | null;
  runtimeConfig: Record<string, unknown> | null;
  budgetMonthlyCents: number | null;
  spentMonthlyCents: number | null;
  permissions: Record<string, unknown> | null;
  lastHeartbeatAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAgentInput {
  companyId: string;
  name: string;
  role?: string;
  title?: string;
  icon?: string;
  department?: string | null;
  avatarUrl?: string;
  runtimeTier?: string;
  reportsTo?: string | null;
  parentAgentId?: string | null;
  capabilities?: string;
  adapterType?: string;
  adapterConfig?: Record<string, unknown>;
  runtimeConfig?: Record<string, unknown>;
  budgetMonthlyCents?: number;
  workspace?: {
    identityMd?: string;
    soulMd?: string;
    userMd?: string;
    agentsMd?: string;
  };
}

export interface WorkspaceFiles {
  identityMd: string;
  soulMd: string;
  userMd: string;
  agentsMd: string;
}

export const agentsApi = {
  listByCompany: (companyId: string) =>
    apiFetch<Agent[]>(`/companies/${companyId}/agents`),
  get: (id: string) => apiFetch<Agent>(`/agents/${id}`),
  create: (data: CreateAgentInput) =>
    apiFetch<Agent>(`/companies/${data.companyId}/agents`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  workspacePreview: (
    companyId: string,
    input: {
      name: string;
      role?: string;
      title?: string;
      department?: string | null;
      capabilities?: string;
      reportsTo?: string | null;
    },
  ) =>
    apiFetch<WorkspaceFiles>(`/companies/${companyId}/agents/workspace-preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  update: (id: string, data: Partial<CreateAgentInput>) =>
    apiFetch<Agent>(`/agents/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiFetch<void>(`/agents/${id}`, { method: "DELETE" }),
  wake: (id: string) =>
    apiFetch<unknown>(`/agents/${id}/wake`, { method: "POST" }),
  healthCheck: (id: string) =>
    apiFetch<{
      success: boolean;
      reachable: boolean;
      gatewayUp?: boolean;
      sidecarUp?: boolean;
      error?: string;
    }>(`/agents/${id}/health-check`, { method: "POST" }),
  generateWorkspace: (id: string) =>
    apiFetch<{ success: boolean; workspace: Record<string, string> }>(
      `/agents/${id}/generate-workspace`,
      { method: "POST" },
    ),
  updateWorkspace: (
    id: string,
    workspace: { soulMd?: string; userMd?: string; identityMd?: string; agentsMd?: string },
  ) =>
    apiFetch<Agent>(`/agents/${id}/workspace`, {
      method: "PATCH",
      body: JSON.stringify(workspace),
    }),
  syncWorkspace: (id: string) =>
    apiFetch<{
      success: boolean;
      regenerated: boolean;
      pushed: boolean;
      pushResults: Array<{ file: string; success: boolean; error?: string }>;
      workspace: Record<string, string>;
    }>(`/agents/${id}/sync-workspace`, { method: "POST" }),
  pushWorkspace: (id: string) =>
    apiFetch<{
      success: boolean;
      pushed: boolean;
      pushResults: Array<{ file: string; success: boolean; error?: string }>;
    }>(`/agents/${id}/push-workspace`, { method: "POST" }),
  /** Rename an agent. Updates DB, rewrites the "Peer Agents" section of
   *  every sibling's AGENTS.md, and best-effort pushes updated files to
   *  each agent's VM. Per-agent push results are returned in
   *  `propagation.pushes` for the UI to surface. */
  rename: (id: string, name: string) =>
    apiFetch<{
      success: boolean;
      renamed: boolean;
      oldName?: string;
      newName?: string;
      agent: Agent;
      propagation: {
        peersUpdated: number;
        pushes: Array<{
          agentId: string;
          agentName: string;
          pushed: boolean;
          reason?: string;
        }>;
      };
    }>(`/agents/${id}/rename`, {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  generateAvatar: (id: string) =>
    apiFetch<{ success: boolean; avatarUrl: string }>(`/agents/${id}/generate-avatar`, { method: "POST" }),
  /** Upload a user-provided avatar. `avatar` must be a data:image/... URI;
   * the caller is responsible for client-side resizing to keep the payload small. */
  uploadAvatar: (id: string, avatar: string) =>
    apiFetch<{ success: boolean; avatarUrl: string }>(`/agents/${id}/upload-avatar`, {
      method: "POST",
      body: JSON.stringify({ avatar }),
    }),
  messageAgent: (agentId: string, targetAgentId: string, message: string) =>
    apiFetch<{ conversationId: string; reply: string; usage?: Record<string, unknown>; model?: string }>(
      `/agents/${agentId}/message-agent`,
      { method: "POST", body: JSON.stringify({ targetAgentId, message }) },
    ),
  listSubagents: (parentAgentId: string) =>
    apiFetch<Agent[]>(`/agents/${parentAgentId}/subagents`),
  createSubagent: (parentAgentId: string, data: Omit<CreateAgentInput, "companyId">) =>
    apiFetch<Agent>(`/agents/${parentAgentId}/subagents`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  removeSubagent: (parentAgentId: string, subagentId: string) =>
    apiFetch<void>(`/agents/${parentAgentId}/subagents/${subagentId}`, { method: "DELETE" }),
};
